/**
 * "ล้างแชทนี้ (ทดสอบ)" (api/app/devreset.py): POST /api/v1/conversations/:id/dev-reset deletes a TEST-account chat
 * so the owner can start the same conversation again from zero. The server decides who/where
 * (ConversationDetail.dev_resettable); every open screen then gets {"type": "conversation.removed"} on the socket.
 * Mock mode: removes the chat from the mock db and emits the same event.
 */
import { ApiError } from './client'
import { reportUnauthorized } from './http'
import { getApi, useMock } from './index'
import { MockChatApi } from './mock'

export interface ConversationRemovedEvent {
  type: 'conversation.removed'
  conversation_id: string
  reason: 'dev_reset'
  by: { id: string; name: string } | null
}

export function isConversationRemovedEvent(v: unknown): v is ConversationRemovedEvent {
  if (typeof v !== 'object' || v === null) return false
  const e = v as { type?: unknown; conversation_id?: unknown }
  return e.type === 'conversation.removed' && typeof e.conversation_id === 'string'
}

// ---- mock: the socket of connectMock (./realtime.ts) forwards these
type Listener = (e: ConversationRemovedEvent) => void
const listeners = new Set<Listener>()
export function onMockRemoved(fn: Listener): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

async function post(path: string, timeoutMs = 15000): Promise<void> {
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), timeoutMs)
  const base = (import.meta.env.VITE_API_BASE ?? '').trim().replace(/\/+$/, '')
  try {
    const res = await fetch(new URL(`${base}/api/v1${path}`, window.location.origin).toString(), {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      signal: ctrl.signal,
    })
    if (res.status === 401) reportUnauthorized()
    if (!res.ok) {
      let detail = res.statusText
      try {
        const j = (await res.json()) as { detail?: unknown }
        if (typeof j.detail === 'string') detail = j.detail
      } catch {
        /* body not JSON */
      }
      throw new ApiError(res.status, detail || `HTTP ${res.status}`)
    }
  } catch (e) {
    if (e instanceof ApiError) throw e
    if (e instanceof DOMException && e.name === 'AbortError') throw new ApiError(0, 'หมดเวลารอเซิร์ฟเวอร์')
    throw new ApiError(0, 'ต่อเซิร์ฟเวอร์ไม่ได้')
  } finally {
    clearTimeout(timer)
  }
}

/** Tests inject their own. */
let impl: ((id: string) => Promise<void>) | null = null
export function setDevReset(fn: ((id: string) => Promise<void>) | null): void {
  impl = fn
}

export async function devResetConversation(id: string): Promise<void> {
  if (impl) return impl(id)
  const api = getApi()
  if (useMock && api instanceof MockChatApi) {
    const c = api.db.conversations.get(id)
    if (!c) throw new ApiError(404, 'ไม่พบห้องแชทนี้')
    if (!c.dev_resettable) throw new ApiError(403, 'ล้างได้เฉพาะแชทของบัญชีทดสอบ แชทนี้ไม่อยู่ในรายชื่อบัญชีทดสอบ')
    await new Promise((r) => setTimeout(r, 250))
    api.db.conversations.delete(id)
    api.db.threads.delete(id)
    const me = api.db.team.find((m) => m.id === api.db.meId)
    const ev: ConversationRemovedEvent = {
      type: 'conversation.removed',
      conversation_id: id,
      reason: 'dev_reset',
      by: me ? { id: me.id, name: me.name } : null,
    }
    for (const fn of listeners) fn(ev)
    return
  }
  await post(`/conversations/${encodeURIComponent(id)}/dev-reset`)
}
